// The first screen, for both kinds: one box, like an AI chat input. The words, then one slim
// toolbar: add your photos, Post or Reel, the options for the kind (each opens its choices in
// place), a proven post to follow, and the send button with its price. The box has the same
// three children whichever kind is on, so sliding the toggle moves nothing.
import { useEffect, useId, useRef, useState } from 'react';
import { LOOKS, REEL_SECONDS, momentAction, momentShapes, type Fidelity } from '@/lib/moment';
import { needsLine, type Following, type Kind } from '@/lib/wizard';

export type Attachment = { id: string; label: string; url: string | null };

export function Moment({
  kind,
  kinds = ['Post', 'Reel'],
  onKind,
  line,
  onLine,
  placeholder,
  error,
  credits,
  spend,
  cards,
  seconds,
  onSeconds,
  fidelity,
  onFidelity,
  shapeId,
  onShape,
  following,
  followingLine,
  onLetGo,
  onProven,
  attachments = [],
  onAttach,
  onDetach,
  busy,
  onGo,
  quiet,
}: {
  kind: Kind;
  /** Which kinds the toggle offers; one kind means no toggle, just its name. */
  kinds?: Kind[];
  onKind: (kind: Kind) => void;
  line: string;
  onLine: (line: string) => void;
  placeholder: string;
  /** The field's own complaint (the idea runs long), or null. */
  error: string | null;
  credits: { balance: number } | null;
  /** What Make it will spend on a post. */
  spend: number;
  /** The cards that spend is for, once the photos are chosen; left out before. */
  cards?: number;
  seconds: 15 | 30;
  onSeconds: (seconds: 15 | 30) => void;
  fidelity: Fidelity;
  onFidelity: (fidelity: Fidelity) => void;
  shapeId: string;
  onShape: (shapeId: string) => void;
  following: Following | null;
  followingLine?: string;
  onLetGo: () => void;
  /** Opens the proven posts to follow; none offered without it. */
  onProven?: () => void;
  /** The host's own photos added to this moment: the AI still makes the picture, from these. */
  attachments?: Attachment[];
  onAttach?: () => void;
  onDetach?: (id: string) => void;
  busy: boolean;
  onGo: () => void;
  quiet: { label: string; onClick: () => void } | null;
}) {
  const action = momentAction(kind, { line, credits, spend, seconds, cards });
  const shapes = momentShapes();
  const shape = shapes.find((s) => s.id === shapeId) ?? shapes[0]!;
  const look = LOOKS.find((l) => l.id === fidelity) ?? LOOKS[0]!;
  return (
    <div className="mo">
      <h1 id="mo-title">What’s the moment?</h1>
      <p className="mo-lede">Describe it like you’d tell a friend. We do the rest.</p>
      <div className="mo-card" role="group" aria-labelledby="mo-title">
        <div className="mo-context">
          {onProven && (
            <button type="button" className="mo-proven" onClick={onProven} disabled={busy}>
              <span aria-hidden>✦</span> {following ? 'Another proven post' : 'Like a proven post'}
            </button>
          )}
          {following && (
            <div className="mo-following" role="group" aria-label="Inspiration reference">
              {following.imageUrl && <img src={following.imageUrl} alt="" />}
              <span>{followingLine ?? `Following @${following.handle}`}</span>
              <button
                type="button"
                className="mo-x"
                aria-label="Remove reference"
                title="Remove reference"
                onClick={onLetGo}
                disabled={busy}
              >
                ×
              </button>
            </div>
          )}
          {attachments.length > 0 && (
            <ul className="mo-attached" aria-label="Your photos">
              {attachments.map((a) => (
                <li key={a.id}>
                  {a.url ? (
                    <img src={a.url} alt={a.label} />
                  ) : (
                    <span role="img" aria-label={a.label} />
                  )}
                  {onDetach && (
                    <button
                      type="button"
                      className="mo-x"
                      aria-label={`Remove ${a.label}`}
                      title="Remove"
                      onClick={() => onDetach(a.id)}
                      disabled={busy}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <textarea
          id="mo-line"
          aria-labelledby="mo-title"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'mo-line-error' : 'mo-note'}
          value={line}
          placeholder={placeholder}
          onChange={(e) => onLine(e.target.value)}
          maxLength={1000}
          rows={2}
          disabled={busy}
        />
        <div className="mo-tools" role="toolbar" aria-label="Options" data-kind={kind}>
          <div className="mo-tools-left">
            {onAttach && (
              <button
                type="button"
                className="mo-icon"
                aria-label="Add your photos"
                title="Add your photos"
                onClick={onAttach}
                disabled={busy}
              >
                +
              </button>
            )}
            {kinds.length > 1 ? (
              <div className="nx-seg small" role="radiogroup" aria-label="Post or Reel">
                {kinds.map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={kind === k}
                    className={kind === k ? 'on' : ''}
                    disabled={busy}
                    onClick={() => kind !== k && onKind(k)}
                  >
                    {k}
                  </button>
                ))}
              </div>
            ) : (
              <span className="mo-kind">{kind}</span>
            )}
            {kind === 'Post' ? (
              <Choice
                label="Shape"
                value={shape.title}
                disabled={busy}
                options={shapes.map((s) => ({
                  id: s.id,
                  label: s.title,
                  hint: [s.blurb, needsLine(s)].filter(Boolean).join(' '),
                }))}
                selected={shape.id}
                onPick={onShape}
              />
            ) : (
              <>
                <Choice
                  label="Length"
                  value={`${seconds} s`}
                  disabled={busy}
                  options={REEL_SECONDS.map((n) => ({
                    id: String(n),
                    label: `${n} s`,
                    name: `${n} seconds`,
                    hint: n === 15 ? 'Six scenes' : 'Twelve scenes, a longer story',
                  }))}
                  selected={String(seconds)}
                  onPick={(v) => onSeconds(Number(v) as 15 | 30)}
                />
                <Choice
                  label="Look"
                  value={look.label}
                  disabled={busy}
                  options={LOOKS.map((l) => ({ id: l.id, label: l.label, hint: l.blurb }))}
                  selected={look.id}
                  onPick={(v) => onFidelity(v as Fidelity)}
                />
              </>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary hv mo-send"
            disabled={busy || action.disabled || !!error}
            onClick={onGo}
          >
            {busy ? 'Starting…' : action.label}
            {!busy && !action.short && <span aria-hidden> ▸</span>}
          </button>
        </div>
      </div>
      {error ? (
        <p id="mo-line-error" className="mo-note err" role="alert">
          {error}
        </p>
      ) : (
        <p id="mo-note" className="mo-note">
          {action.note}
        </p>
      )}
      {quiet && (
        <button type="button" className="nx-quiet mo-quiet" onClick={quiet.onClick} disabled={busy}>
          {quiet.label}
        </button>
      )}
    </div>
  );
}

type Option = { id: string; label: string; name?: string; hint?: string };

/** One quiet control in the toolbar: its value on a pill; its choices open in place. */
function Choice({
  label,
  value,
  options,
  selected,
  onPick,
  disabled,
}: {
  label: string;
  value: string;
  options: Option[];
  selected: string;
  onPick: (id: string) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const pill = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      pill.current?.focus();
    };
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);
  return (
    <div className="mo-pick" ref={box}>
      <button
        ref={pill}
        type="button"
        className="mo-pill"
        aria-label={`${label}: ${value}`}
        aria-haspopup="true"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {label === 'Shape' && <span className="mo-pill-k">Shape</span>}
        {value}
        <span className="mo-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="mo-menu" role="radiogroup" aria-label={label}>
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={o.id === selected}
              aria-label={o.name ?? o.label}
              aria-describedby={o.hint ? `${id}-${o.id}` : undefined}
              className={o.id === selected ? 'on' : ''}
              onClick={() => {
                onPick(o.id);
                setOpen(false);
                pill.current?.focus();
              }}
            >
              <span className="t">{o.label}</span>
              {o.hint && (
                <span className="h" id={`${id}-${o.id}`}>
                  {o.hint}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
